import React from 'react';
import type { AtlasViewConfig, ComposedLevelFilters } from 'types/contextView';
import type { UnifiedFilters } from 'types/filters';
import type { FilterSaveTarget, FilterSaveTargetAvailability } from '../../../hooks/useLevelFilters';
import type { LevelFilterTierRow } from '../../../utils/composeLevelFilters';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import type { SchemaForFilters } from '../../../utils/filterPropertyBuilder';

export type ResponsiveBreakpoint = 'mobile' | 'minimal' | 'compact' | 'full';

export interface AtlasViewContextValue {
  contextSystemName: string;
  viewConfig: AtlasViewConfig;
  containerWidth: number;
  responsiveBreakpoint: ResponsiveBreakpoint;
  /**
   * The view-wide (`'*'`) composed filter stack (`useLevelFilters`) an
   * Aspect column's `LevelFilterPopoverOrganism` narrows to its own
   * property via `focusProperty` — see `atlas-header.tsx`. Optional: only
   * `AtlasComponent`'s real provider supplies it — `AtlasLivePreviewMolecule`
   * (the view editor's inert preview) has no view/save-target of its own, so
   * `AtlasTree`/`atlas-header` default these to an inert empty stack.
   */
  composedColumnFilters?: ComposedLevelFilters;
  columnFilterSaveTargets?: FilterSaveTargetAvailability[];
  onColumnFilterSave?: (filters: UnifiedFilters, target: FilterSaveTarget) => void;
  activeViewDisplayName?: string;
  /**
   * The view-wide scope's context tier, narrowed to whichever column's
   * popover is currently open (`onColumnFilterOpen` below) — passed through
   * to that popover as `contextTierRows` so it can show the context's own
   * clauses on this aspect and flag a conflict against them (EAINT-931 W6:
   * "SC7 context tier visible for Atlas"). Empty while no popover is open.
   * Optional for the same reason `composedColumnFilters` is:
   * `AtlasLivePreviewMolecule` has no context definition of its own.
   */
  globalContextTiers?: LevelFilterTierRow[];
  /**
   * Tells the shared `useLevelFilters` hook which column's aspect is now
   * open, so it can narrow `globalContextTiers` to that column
   * (`LevelFilterPopoverOrganism`'s `onOpen`, EAINT-931 W6). Called with
   * `undefined` on close (`onHide`) so `globalContextTiers` returns to empty
   * rather than staying narrowed to the last-opened column.
   */
  onColumnFilterOpen?: (aspect: string | undefined) => void;
  /**
   * Arc templates in view — the Stage column's `LevelFilterPopoverOrganism`
   * (`focusArc="primary"`) needs these to populate its template/stage picker.
   * Optional for the same reason `composedColumnFilters` is: a mount with no
   * real host (`AtlasLivePreviewMolecule`) has none to supply.
   */
  arcTemplates?: ArcTemplateOption[];
  /** Schemas in scope — forwarded to every column popover's catalog (EAINT-936). */
  allSchemas?: SchemaForFilters[];
  /**
   * `PerspectiveValueEditorMolecule`'s people-search config — the sphere/org
   * a perspective column's "who" search runs against. Optional for the same
   * reason `composedColumnFilters` is: a mount with no real host has no
   * sphere to search from.
   */
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
    direction?: 'outwards' | 'inwards' | 'both';
    hops?: number;
  };
  /**
   * Fires when a column popover's "Share with view…" is confirmed — receives
   * the column's own user-tier clauses. `AtlasComponent` wires this to its
   * own `useLevelFilters` buffer + commit (EAINT-936); optional for the same
   * reason every other host-owned handler here is.
   */
  onShareWithView?: (clauses: UnifiedFilters) => void | Promise<void>;
  /** Fires when `onShareWithView` rejects with a 403 — bumps the permission-cache epoch. */
  onViewPermissionDenied?: () => void;
}

export const AtlasViewContext = React.createContext<AtlasViewContextValue | undefined>(undefined);

export const getResponsiveBreakpoint = (width: number): ResponsiveBreakpoint => {
  if (width === 0) return 'full';
  if (width < 500) return 'mobile';
  if (width < 800) return 'minimal';
  if (width < 1200) return 'compact';
  return 'full';
};
